"use client";

import Link from "next/link";
import { QUANT_INTRO_OFFER } from "@/config/subscriptions";
import { useCountdown } from "@/hooks/use-countdown";
import { Icons } from "@/components/shared/icons";
import { cn } from "@/lib/utils";

// QW Intelligence "liquid metal" text (brand guide): brushed silver with an
// emerald glint. Only used on the dark Quant banner so it reads in both themes.
export const metalText: React.CSSProperties = {
  background: "linear-gradient(100deg, #f2f4f3, #9aa09e 40%, #e8eae9 55%, #7fe7c8 130%)",
  WebkitBackgroundClip: "text",
  backgroundClip: "text",
  WebkitTextFillColor: "transparent",
};

const pad = (n: number) => String(n).padStart(2, "0");

/** Plain emerald check - no circle, per design feedback. */
export function CheckChip() {
  return (
    <Icons.check className="mt-0.5 size-4 shrink-0 text-emerald-500" aria-hidden="true" />
  );
}

/** Wraps a keyword of a benefit line in a link to its landing page. */
function LinkedKeyword({ text, keyword, href }: { text: string; keyword: string; href: string }) {
  const [before, after] = text.split(keyword);
  return (
    <>
      {before}
      <Link
        href={href}
        className="underline decoration-emerald-500/50 decoration-2 underline-offset-2 transition-colors hover:decoration-emerald-500"
      >
        {keyword}
      </Link>
      {after}
    </>
  );
}

export function FeatureText({ text }: { text: string }) {
  if (text.includes("Full Wheel Lifecycle")) {
    const [before, after] = text.split("Full Wheel Lifecycle");
    return <>{before}<span className="underline decoration-primary/50 decoration-2 underline-offset-2">Full Wheel Lifecycle</span>{after}</>;
  }
  if (text.includes("GEX PlayTape")) {
    const [before, after] = text.split("GEX PlayTape");
    return <>{before}<span className="underline decoration-zinc-400/60 decoration-2 underline-offset-2">GEX PlayTape</span>{after}</>;
  }
  if (text.startsWith("Options Flows")) {
    return <LinkedKeyword text={text} keyword="Options Flows" href="/options-flows-api" />;
  }
  if (text.includes("Full MCP access")) {
    return <LinkedKeyword text={text} keyword="Full MCP access" href="/mcp" />;
  }
  if (text.includes("MCP access")) {
    return <LinkedKeyword text={text} keyword="MCP access" href="/mcp" />;
  }
  if (text.includes("Full REST API access")) {
    return <LinkedKeyword text={text} keyword="Full REST API access" href="/api" />;
  }
  if (text.includes("TradingView overlay")) {
    return <LinkedKeyword text={text} keyword="TradingView overlay" href="/tradingview-gex-bridge" />;
  }
  return <>{text}</>;
}

// Launch-time floor: always show at least this many spots claimed, even
// before the seats API responds (or while real numbers are lower).
const FAKE_CLAIMED_FLOOR = 7;

/** Spots still available at the intro price, honoring the fake claimed floor. */
export function quantSpotsLeft(seatsLeft: number | null): number {
  const limit = QUANT_INTRO_OFFER.seatLimit;
  const realClaimed = seatsLeft === null ? 0 : Math.max(0, limit - seatsLeft);
  const claimed = Math.min(limit, Math.max(FAKE_CLAIMED_FLOOR, realClaimed));
  return limit - claimed;
}

/** Seats indicator on the dark Quant banner: "N left", "claimed/limit" + bar. */
export function QuantSeatsMeter({ seatsLeft }: { seatsLeft: number | null }) {
  const limit = QUANT_INTRO_OFFER.seatLimit;
  const left = quantSpotsLeft(seatsLeft);
  const claimed = limit - left;

  return (
    <div className="mt-4">
      <div className="flex items-baseline justify-between text-[11px]">
        <span className="text-white/60">
          <strong className="font-semibold text-emerald-300">{left}</strong> left at this price
        </span>
        <span className="tabular-nums text-white/40">
          {claimed}/{limit} claimed
        </span>
      </div>
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
        <div
          className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-emerald-500 transition-[width] duration-500"
          style={{ width: `${(claimed / limit) * 100}%` }}
        />
      </div>
    </div>
  );
}

/** Quant-only quarterly billing opt-in, rendered on the dark Quant banner. */
export function QuantQuarterlyToggle({
  price,
  selected,
  onToggle,
  className,
}: {
  price: number;
  selected: boolean;
  onToggle: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={selected}
      className={cn(
        "flex w-full items-center gap-2.5 rounded-xl border px-3 py-2 text-left text-xs transition-colors",
        selected
          ? "border-emerald-400/70 bg-emerald-500/10"
          : "border-white/15 hover:border-emerald-400/50",
        className,
      )}
    >
      <span
        className={cn(
          "size-3 shrink-0 rounded-full border-2",
          selected ? "border-emerald-400 bg-emerald-400" : "border-white/40",
        )}
      />
      <span>
        <span className="font-medium text-white">Quarterly: ${price} / quarter</span>{" "}
        {/*<span className="text-white/55">
          every 3 months (~${Math.floor(price / 3)}/mo)
        </span>*/}
      </span>
    </button>
  );
}

/**
 * Quiet countdown strip pinned to the top of the Quant card: no border box,
 * no animation - just a hairline separator and mono digits.
 */
export function QuantCountdown() {
  const timeLeft = useCountdown(QUANT_INTRO_OFFER.endsAt);
  if (timeLeft.expired) return null;
  return (
    <div className="flex items-center justify-between gap-3 border-b border-white/[0.08] bg-[#0d0e10] px-7 py-2.5">
      <span className="text-[10px] uppercase tracking-[0.15em] text-white/40">
        Intro pricing ends in
      </span>
      <span className="font-mono text-xs tabular-nums text-emerald-300/90">
        {timeLeft.days}d {pad(timeLeft.hours)}:{pad(timeLeft.minutes)}:{pad(timeLeft.seconds)}
      </span>
    </div>
  );
}
